import {
  CalendarCheck2,
  CalendarDays,
  CircleAlert,
  CircleCheck,
  Clock3,
  Plus,
  RotateCcw,
  Search,
  SearchX,
  Sparkles,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAuth } from '../auth/AuthProvider'
import { useTenant } from '../tenant/TenantProvider'
import { Button } from '../../ui/Button/Button'
import { Skeleton } from '../../ui/Skeleton/Skeleton'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import {
  createAppointmentActionsRepository,
  type AppointmentActionsRepository,
} from './appointment-actions-repository'
import {
  createAppointmentBookingRepository,
  type AppointmentBookingRepository,
} from './appointment-booking-repository'
import styles from './AppointmentsPage.module.css'
import {
  formatAppointmentRowParts,
  pmsPresentation,
  type AppointmentItem,
} from './appointments-model'
import {
  firebaseAppointmentsRepository,
  type AppointmentWindowRepository,
  type AppointmentsRepository,
} from './appointments-repository'
import { useAppointments } from './use-appointments'
import {
  useAppointmentTimeZone,
  useAppointmentWindow,
} from './use-appointment-window'
import { AppointmentDetailModal } from './components/AppointmentDetailModal'
import { createBookingChecksRepository } from '../booking-checks/booking-checks-repository'
import type { BookingChecksRepository } from '../booking-checks/booking-checks-repository'
import { AppointmentPmsBadge } from './components/AppointmentPmsBadge'
import { AppointmentStatusBadge } from './components/AppointmentStatusBadge'
import { BookAppointmentDialog } from './components/BookAppointmentDialog'
import {
  createPatientsRepository,
  type PatientsRepository,
} from '../patients/patients-repository'
import type {
  PatientRecord,
  ServiceIntent,
} from '../patients/patients-contracts'
import { usePatient, usePatients } from '../patients/use-patients'
import {
  createProviderResourceRepository,
  type ProviderResourceRepository,
} from '../settings/provider-resource-repository'
import { useBookingCapability } from './use-booking-capability'
import { useAppointmentSync } from './use-appointment-sync'

export const APPOINTMENT_BOUNDARY_REFRESH_MS = 60_000
const KPI_WINDOW_DAYS = 42
const DAY_MS = 86_400_000
const SKELETON_ROWS = 6
type TimeFilter = 'today' | 'tomorrow' | 'week' | 'upcoming' | 'past' | 'all'

const PERIODS: Array<[TimeFilter, string]> = [
  ['today', 'Today'],
  ['tomorrow', 'Tomorrow'],
  ['week', 'Next 7 days'],
  ['upcoming', 'Upcoming'],
  ['past', 'Past'],
  ['all', 'All'],
]

const PERIOD_EMPTY: Record<TimeFilter, string> = {
  today: 'No appointments today',
  tomorrow: 'No appointments tomorrow',
  week: 'No appointments in the next 7 days',
  upcoming: 'No upcoming appointments',
  past: 'No past appointments',
  all: 'No appointments yet',
}

function dayKey(timestamp: number, timeZone: string | null) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone ?? 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(timestamp)
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(timestamp)
  }
}

function addCalendarDays(date: string, count: number) {
  return new Date(Date.parse(`${date}T00:00:00Z`) + count * DAY_MS)
    .toISOString()
    .slice(0, 10)
}

// Booked by Flio: the channels Flio books through (the source filter's rule).
const FLIO_BOOKING_SOURCES: readonly AppointmentItem['source'][] = [
  'sms',
  'voice',
  'dashboard',
]

function bookedByFlio(item: AppointmentItem) {
  return FLIO_BOOKING_SOURCES.includes(item.source)
}

// Every card counts the same appointments: upcoming (canonical time from now
// to the end of the window) and not cancelled.
function upcomingAppointmentKpis(
  nowMs: number,
  primary: AppointmentItem[],
  boundary: AppointmentItem[]
) {
  const endBeforeMs = nowMs + KPI_WINDOW_DAYS * DAY_MS
  const appointments = new Map<string, AppointmentItem>()
  for (const item of [...primary, ...boundary]) {
    if (
      item.temporalConfidence === 'canonical' &&
      item.scheduledAtMs !== null &&
      item.scheduledAtMs >= nowMs &&
      item.scheduledAtMs < endBeforeMs &&
      item.status !== 'cancelled'
    ) {
      appointments.set(item.appointmentId, item)
    }
  }
  const values = [...appointments.values()]
  const confirmed = values.filter((item) => item.status === 'confirmed').length
  return {
    upcoming: values.length,
    confirmed,
    notConfirmed: values.length - confirmed,
    flio: values.filter(bookedByFlio).length,
  }
}

function Metric({
  label,
  value,
  caption,
  icon: Icon,
  tone,
}: {
  label: string
  value: number | null
  caption: string
  icon: LucideIcon
  tone: 'info' | 'success' | 'primary' | 'warning'
}) {
  return (
    <div className={styles.metric} data-tone={tone}>
      <span className={styles.metricLabel}>
        <span className={styles.metricIcon} aria-hidden="true">
          <Icon size={15} strokeWidth={2} />
        </span>
        {label}
      </span>
      <strong>{value === null ? '—' : value.toLocaleString()}</strong>
      <small>{caption}</small>
    </div>
  )
}

function LoadingRows() {
  return (
    <>
      <p className={styles.srOnly} role="status">
        Loading appointments
      </p>
      <ul className={styles.rows} aria-hidden="true">
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <li key={index} className={`${styles.row} ${styles.skeletonRow}`}>
            <span className={styles.stack}>
              <Skeleton width="4.5rem" height="0.75rem" />
              <Skeleton width="3.5rem" height="0.625rem" />
            </span>
            <span className={styles.stack}>
              <Skeleton width="9rem" height="0.875rem" />
              <Skeleton width="6rem" height="0.625rem" />
            </span>
            <span className={`${styles.stack} ${styles.providerCell}`}>
              <Skeleton width="7rem" height="0.75rem" />
              <Skeleton width="4rem" height="0.625rem" />
            </span>
            <span className={styles.stack}>
              <Skeleton width="5.5rem" height="1.25rem" />
            </span>
            <span className={`${styles.stack} ${styles.sourceCell}`}>
              <Skeleton width="7.5rem" height="0.75rem" />
            </span>
          </li>
        ))}
      </ul>
    </>
  )
}

function AppointmentRow({
  appointment,
  nowMs,
  onOpen,
}: {
  appointment: AppointmentItem
  nowMs: number
  onOpen: () => void
}) {
  const parts =
    appointment.scheduledAtMs === null
      ? null
      : formatAppointmentRowParts(
          appointment.scheduledAtMs,
          appointment.clinicTimeZone,
          nowMs
        )
  const pms = pmsPresentation(appointment, nowMs)
  const attention = parts === null || pms?.needsAttention === true
  const room = appointment.operatoryName
  return (
    <li
      className={styles.row}
      data-attention={attention || undefined}
      data-cancelled={appointment.status === 'cancelled' || undefined}
    >
      <button
        type="button"
        className={styles.rowOpen}
        aria-label={`${appointment.patientDisplayName}, ${
          parts ? `${parts.day} ${parts.time}` : 'time needs review'
        }, ${appointment.statusLabel}`}
        onClick={onOpen}
      />
      <span className={styles.stack}>
        {parts ? (
          <>
            <time
              className={styles.primaryText}
              dateTime={new Date(appointment.scheduledAtMs!).toISOString()}
            >
              {parts.time}
            </time>
            <small>{parts.day}</small>
          </>
        ) : (
          <>
            <span className={styles.primaryText}>Time needs review</span>
            {appointment.scheduledWallTime ? (
              <small>{appointment.scheduledWallTime}</small>
            ) : null}
          </>
        )}
      </span>
      <span className={styles.stack}>
        <strong className={styles.patientName}>
          {appointment.patientDisplayName}
        </strong>
        {appointment.appointmentTypeName ? (
          <small>{appointment.appointmentTypeName}</small>
        ) : null}
      </span>
      <span className={`${styles.stack} ${styles.providerCell}`}>
        {appointment.providerName ? (
          <span className={styles.secondaryText}>
            {appointment.providerName}
          </span>
        ) : null}
        {room ? <small>{room}</small> : null}
        {!appointment.providerName && !room ? (
          <small>Provider details unavailable</small>
        ) : null}
      </span>
      <span className={styles.statusCell}>
        <AppointmentStatusBadge appointment={appointment} />
        {pms && pms.label !== 'In clinic system' ? (
          <AppointmentPmsBadge appointment={appointment} nowMs={nowMs} />
        ) : null}
      </span>
      <span className={`${styles.stack} ${styles.sourceCell}`}>
        <small>{appointment.sourceLabel}</small>
      </span>
    </li>
  )
}

export function AppointmentsContent({
  tenantId,
  repository,
  actionRepository,
  bookingRepository,
  patientsRepository,
  providerResourceRepository,
  patients = [],
  serviceIntents = [],
  patientsStatus = 'ready',
  now = Date.now,
  boundaryRefreshMs = APPOINTMENT_BOUNDARY_REFRESH_MS,
  patientContext = null,
  onNavigate = () => undefined,
  onRetry,
  bookingChecksRepository = null,
}: {
  tenantId: string
  repository: AppointmentsRepository & AppointmentWindowRepository
  actionRepository?: AppointmentActionsRepository
  bookingRepository?: AppointmentBookingRepository
  patientsRepository?: PatientsRepository
  providerResourceRepository?: ProviderResourceRepository
  patients?: PatientRecord[]
  serviceIntents?: ServiceIntent[]
  patientsStatus?: ReturnType<typeof usePatients>['status']
  now?: () => number
  boundaryRefreshMs?: number
  patientContext?: PatientRecord | null
  onNavigate?: (path: string) => void
  onRetry?: () => void
  bookingChecksRepository?: BookingChecksRepository | null
}) {
  const [currentNowMs, setCurrentNowMs] = useState(() => now())
  const upcoming = useAppointments(
    repository,
    tenantId,
    'upcoming',
    currentNowMs
  )
  const past = useAppointments(
    repository,
    tenantId,
    'past',
    currentNowMs,
    false
  )
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('upcoming')
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [providerFilter, setProviderFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedSnapshot, setSelectedSnapshot] =
    useState<AppointmentItem | null>(null)
  const [bookingOpen, setBookingOpen] = useState(false)
  const metricTimeZone = useAppointmentTimeZone(repository, tenantId)
  const metricStartDate = dayKey(currentNowMs, metricTimeZone.timeZone)
  const metricDates = useMemo(
    () =>
      Array.from({ length: KPI_WINDOW_DAYS }, (_, index) =>
        addCalendarDays(metricStartDate, index)
      ),
    [metricStartDate]
  )
  const metricBoundaryDate = addCalendarDays(metricStartDate, KPI_WINDOW_DAYS)
  const metricWindow = useAppointmentWindow(repository, tenantId, metricDates)
  const metricBoundaryWindow = useAppointmentWindow(repository, tenantId, [
    metricBoundaryDate,
  ])
  // The clinic days the selected period shows (the next seven for upcoming).
  const syncDates = useMemo(() => {
    if (!metricTimeZone.timeZone) return []
    if (timeFilter === 'tomorrow') return [addCalendarDays(metricStartDate, 1)]
    if (timeFilter === 'week' || timeFilter === 'upcoming') {
      return Array.from({ length: 7 }, (_, index) =>
        addCalendarDays(metricStartDate, index)
      )
    }
    return [metricStartDate]
  }, [metricStartDate, metricTimeZone.timeZone, timeFilter])
  const syncNotice = useAppointmentSync(
    repository,
    tenantId,
    syncDates,
    metricTimeZone.timeZone,
    currentNowMs
  )
  const bookingCapability = useBookingCapability(
    providerResourceRepository,
    tenantId
  )
  const bookingAvailable =
    Boolean(bookingRepository) &&
    bookingCapability.bookAllowed &&
    patientsStatus === 'ready'
  // Said only when booking is unavailable, and never while it is being checked.
  const bookingNote =
    bookingAvailable || bookingCapability.state === 'loading'
      ? null
      : !bookingRepository && bookingCapability.bookAllowed
        ? 'Booking is unavailable right now.'
        : bookingCapability.bookAllowed
          ? null
          : bookingCapability.bookingMessage

  useEffect(() => {
    const timer = window.setInterval(
      () => setCurrentNowMs(now()),
      boundaryRefreshMs
    )
    return () => window.clearInterval(timer)
  }, [boundaryRefreshMs, now])
  useEffect(() => {
    if (patientContext && bookingAvailable) setBookingOpen(true)
  }, [bookingAvailable, patientContext])
  useEffect(() => {
    if (!bookingAvailable) setBookingOpen(false)
  }, [bookingAvailable])

  const loaded = useMemo(() => {
    const values = new Map<string, AppointmentItem>()
    ;[
      ...upcoming.appointments,
      ...past.appointments,
      ...upcoming.reviewAppointments,
    ].forEach((item) => values.set(item.appointmentId, item))
    return Array.from(values.values()).sort(
      (a, b) =>
        (a.scheduledAtMs ?? Number.MAX_SAFE_INTEGER) -
        (b.scheduledAtMs ?? Number.MAX_SAFE_INTEGER)
    )
  }, [past.appointments, upcoming.appointments, upcoming.reviewAppointments])
  const providers = useMemo(
    () =>
      Array.from(
        new Set(loaded.map((item) => item.providerName).filter(Boolean))
      ).sort() as string[],
    [loaded]
  )
  const types = useMemo(
    () =>
      Array.from(
        new Set(loaded.map((item) => item.appointmentTypeName).filter(Boolean))
      ).sort() as string[],
    [loaded]
  )
  const clinicZone =
    loaded.find((item) => item.clinicTimeZone)?.clinicTimeZone ?? null
  const today = dayKey(currentNowMs, clinicZone)
  const tomorrow = dayKey(currentNowMs + 86_400_000, clinicZone)
  const weekEnd = currentNowMs + 7 * 86_400_000
  const needle = query.trim().toLocaleLowerCase()
  const filtersActive =
    Boolean(needle) ||
    statusFilter !== 'all' ||
    sourceFilter !== 'all' ||
    providerFilter !== 'all' ||
    typeFilter !== 'all'
  const inPeriod = (item: AppointmentItem) => {
    const timestamp = item.scheduledAtMs
    const itemDay =
      timestamp === null ? null : dayKey(timestamp, item.clinicTimeZone)
    return (
      timeFilter === 'all' ||
      (timeFilter === 'today' && itemDay === today) ||
      (timeFilter === 'tomorrow' && itemDay === tomorrow) ||
      (timeFilter === 'week' &&
        timestamp !== null &&
        timestamp >= currentNowMs &&
        timestamp < weekEnd) ||
      (timeFilter === 'upcoming' &&
        timestamp !== null &&
        timestamp >= currentNowMs) ||
      (timeFilter === 'past' && timestamp !== null && timestamp < currentNowMs)
    )
  }
  const periodItems = loaded.filter(inPeriod)
  const visible = periodItems.filter((item) => {
    const search =
      !needle ||
      [
        item.patientDisplayName,
        item.appointmentTypeName,
        item.providerName,
        item.operatoryName,
      ]
        .filter(Boolean)
        .some((value) => value!.toLocaleLowerCase().includes(needle))
    const source =
      sourceFilter === 'all' ||
      (sourceFilter === 'flio' && bookedByFlio(item)) ||
      item.source === sourceFilter
    return (
      search &&
      source &&
      (statusFilter === 'all' || item.status === statusFilter) &&
      (providerFilter === 'all' || item.providerName === providerFilter) &&
      (typeFilter === 'all' || item.appointmentTypeName === typeFilter)
    )
  })
  const status =
    upcoming.status === 'forbidden' || past.status === 'forbidden'
      ? 'forbidden'
      : upcoming.status === 'error' || past.status === 'error'
        ? 'error'
        : upcoming.status === 'invalid' || past.status === 'invalid'
          ? 'invalid'
          : upcoming.status === 'loading' || past.status === 'loading'
            ? 'loading'
            : 'ready'
  const kpis =
    metricWindow.status === 'ready' && metricBoundaryWindow.status === 'ready'
      ? upcomingAppointmentKpis(
          currentNowMs,
          metricWindow.appointments,
          metricBoundaryWindow.appointments
        )
      : null
  const invalidCount = upcoming.invalidCount + past.invalidCount
  // The open detail follows the live record; the snapshot covers a row that
  // left the loaded window while it was open.
  const selected = selectedId
    ? (loaded.find((item) => item.appointmentId === selectedId) ??
      selectedSnapshot)
    : null
  const dialogOpen = Boolean(selected) || bookingOpen
  const showColumns =
    status === 'loading' || (status === 'ready' && visible.length > 0)
  const showUpcomingMore =
    upcoming.hasMore && timeFilter !== 'past' && timeFilter !== 'today'
  const showPastMore =
    past.hasMore && (timeFilter === 'past' || timeFilter === 'all')

  function clearFilters() {
    setQuery('')
    setStatusFilter('all')
    setSourceFilter('all')
    setProviderFilter('all')
    setTypeFilter('all')
  }

  return (
    <div className={styles.page}>
      <div
        className={styles.pageContent}
        {...((dialogOpen ? { inert: '' } : {}) as { inert?: string })}
        aria-hidden={dialogOpen || undefined}
      >
        <section className={styles.metrics} aria-label="Appointment metrics">
          <Metric
            label="Upcoming appointments"
            value={kpis?.upcoming ?? null}
            caption="Next 6 weeks"
            icon={CalendarDays}
            tone="info"
          />
          <Metric
            label="Confirmed"
            value={kpis?.confirmed ?? null}
            caption="Next 6 weeks"
            icon={CircleCheck}
            tone="success"
          />
          <Metric
            label="Not confirmed"
            value={kpis?.notConfirmed ?? null}
            caption="Next 6 weeks"
            icon={Clock3}
            tone="warning"
          />
          <Metric
            label="Booked by Flio"
            value={kpis?.flio ?? null}
            caption="Next 6 weeks"
            icon={Sparkles}
            tone="primary"
          />
        </section>

        <section
          className={styles.directoryPanel}
          aria-label="Appointment directory"
        >
          <header className={styles.directoryHeader}>
            <div className={styles.directoryHeading}>
              <span className={styles.eyebrow}>Clinic schedule</span>
              <h2>Appointment list</h2>
            </div>
            <div className={styles.headerActions}>
              <Button
                type="button"
                className={styles.bookButton}
                disabled={!bookingAvailable}
                aria-describedby={bookingNote ? 'booking-note' : undefined}
                onClick={() => setBookingOpen(true)}
              >
                <Plus size={16} aria-hidden="true" /> Book appointment
              </Button>
              {bookingNote ? (
                <p id="booking-note" className={styles.bookingNote}>
                  {bookingNote}
                </p>
              ) : null}
            </div>
          </header>

          {syncNotice?.tone === 'warning' ? (
            <p className={styles.notice} data-tone="warning" role="status">
              <TriangleAlert size={15} aria-hidden="true" />
              <span>{syncNotice.message}</span>
            </p>
          ) : null}
          {invalidCount > 0 ? (
            <p className={styles.notice} data-tone="warning" role="status">
              <CircleAlert size={15} aria-hidden="true" />
              <span>
                Some appointment records couldn’t be shown because they’re
                incomplete.
              </span>
            </p>
          ) : null}

          <div className={styles.toolbar} aria-label="Appointment toolbar">
            <label className={styles.searchBox}>
              <Search size={16} aria-hidden="true" />
              <span className={styles.srOnly}>Search appointments</span>
              <input
                type="search"
                value={query}
                placeholder="Search patient, visit, provider or room"
                onChange={(event) => setQuery(event.target.value)}
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              ) : null}
            </label>
            <div
              className={styles.periodBar}
              role="group"
              aria-label="Appointment period"
            >
              {PERIODS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={timeFilter === value}
                  onClick={() => setTimeFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div
            className={styles.filterRow}
            role="group"
            aria-label="Appointment filters"
          >
            <select
              aria-label="Filter status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="unknown">Status unavailable</option>
            </select>
            <select
              aria-label="Filter source"
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value)}
            >
              <option value="all">All sources</option>
              <option value="flio">Booked by Flio</option>
              <option value="unknown">Practice system</option>
              <option value="widget">Web widget</option>
            </select>
            <select
              aria-label="Filter provider"
              value={providerFilter}
              onChange={(event) => setProviderFilter(event.target.value)}
            >
              <option value="all">All providers</option>
              {providers.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <select
              aria-label="Filter visit type"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value="all">All visit types</option>
              {types.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            {filtersActive ? (
              <Button
                type="button"
                variant="ghost"
                className={styles.clearFilters}
                onClick={clearFilters}
              >
                Clear filters
              </Button>
            ) : null}
          </div>

          {showColumns ? (
            <div className={styles.tableHeader} aria-hidden="true">
              <span>Time</span>
              <span>Patient &amp; visit</span>
              <span className={styles.providerCell}>Provider &amp; room</span>
              <span>Status</span>
              <span className={styles.sourceCell}>Source</span>
            </div>
          ) : null}
          <div className={styles.listBody} aria-busy={status === 'loading'}>
            {status === 'loading' ? (
              <LoadingRows />
            ) : status === 'forbidden' ? (
              <div className={styles.stateContainer}>
                <StatePanel
                  kind="forbidden"
                  title="Appointments unavailable"
                  description="You don’t have access to this clinic’s appointments."
                />
              </div>
            ) : status !== 'ready' ? (
              <div className={styles.stateContainer}>
                <StatePanel
                  kind="error"
                  title="Appointments unavailable"
                  description="Appointments couldn’t be loaded."
                />
                {onRetry ? (
                  <Button type="button" variant="secondary" onClick={onRetry}>
                    <RotateCcw size={15} aria-hidden="true" /> Try again
                  </Button>
                ) : null}
              </div>
            ) : visible.length === 0 ? (
              <div className={styles.emptyState}>
                <span className={styles.emptyIcon} aria-hidden="true">
                  {filtersActive ? (
                    <SearchX size={22} />
                  ) : (
                    <CalendarCheck2 size={22} />
                  )}
                </span>
                <h3>
                  {filtersActive
                    ? 'No appointments match'
                    : PERIOD_EMPTY[timeFilter]}
                </h3>
                <p>
                  {filtersActive
                    ? 'Check the spelling or clear the filters.'
                    : periodItems.length === 0 && loaded.length > 0
                      ? 'Choose another period to see more appointments.'
                      : 'Appointments booked in your practice system appear here after the next sync.'}
                </p>
                {filtersActive ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={clearFilters}
                  >
                    Clear search and filters
                  </Button>
                ) : null}
              </div>
            ) : (
              <ul className={styles.rows}>
                {visible.map((item) => (
                  <AppointmentRow
                    key={item.appointmentId}
                    appointment={item}
                    nowMs={currentNowMs}
                    onOpen={() => {
                      setSelectedId(item.appointmentId)
                      setSelectedSnapshot(item)
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
          {status === 'ready' &&
          (visible.length > 0 || showUpcomingMore || showPastMore) ? (
            <footer className={styles.directoryFooter}>
              <span className={styles.rangeLabel}>
                {visible.length.toLocaleString()}{' '}
                {visible.length === 1 ? 'appointment' : 'appointments'}
                {filtersActive ? ' · filtered' : ''}
              </span>
              <span className={styles.footerActions}>
                {showUpcomingMore ? (
                  <Button
                    variant="secondary"
                    type="button"
                    disabled={upcoming.loadingMore}
                    onClick={() => void upcoming.loadMore()}
                  >
                    Load more upcoming
                  </Button>
                ) : null}
                {showPastMore ? (
                  <Button
                    variant="secondary"
                    type="button"
                    disabled={past.loadingMore}
                    onClick={() => void past.loadMore()}
                  >
                    Load more history
                  </Button>
                ) : null}
              </span>
            </footer>
          ) : null}
        </section>
      </div>
      {selected ? (
        <AppointmentDetailModal
          key={selected.appointmentId}
          appointment={selected}
          tenantId={tenantId}
          actionRepository={actionRepository}
          bookingCapability={bookingCapability}
          patients={patients}
          nowMs={currentNowMs}
          onNavigate={onNavigate}
          bookingChecksRepository={bookingChecksRepository}
          onClose={() => {
            setSelectedId(null)
            setSelectedSnapshot(null)
          }}
        />
      ) : null}
      {bookingRepository &&
      patientsRepository &&
      bookingCapability.bookAllowed ? (
        <BookAppointmentDialog
          open={bookingOpen}
          tenantId={tenantId}
          patientsRepository={patientsRepository}
          serviceIntents={serviceIntents}
          repository={bookingRepository}
          initialPatient={patientContext}
          onClose={() => setBookingOpen(false)}
          onBooked={() => {
            setCurrentNowMs(now())
          }}
        />
      ) : null}
    </div>
  )
}

export function AppointmentsPage({
  repository = firebaseAppointmentsRepository,
  actionRepository,
  patientsRepository,
  bookingRepository,
  providerResourceRepository,
}: {
  repository?: AppointmentsRepository & AppointmentWindowRepository
  actionRepository?: AppointmentActionsRepository
  patientsRepository?: PatientsRepository
  bookingRepository?: AppointmentBookingRepository
  providerResourceRepository?: ProviderResourceRepository
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [attempt, setAttempt] = useState(0)
  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const actions = useMemo(
    () =>
      actionRepository ??
      createAppointmentActionsRepository({ getUser: () => currentUser }),
    [actionRepository, currentUser]
  )
  const patientSource = useMemo(
    () =>
      patientsRepository ??
      createPatientsRepository({ getUser: () => currentUser }),
    [currentUser, patientsRepository]
  )
  const booking = useMemo(
    () =>
      bookingRepository ??
      createAppointmentBookingRepository({ getUser: () => currentUser }),
    [bookingRepository, currentUser]
  )
  const bookingCapabilitySource = useMemo(
    () =>
      providerResourceRepository ??
      createProviderResourceRepository({ getUser: () => currentUser }),
    [currentUser, providerResourceRepository]
  )
  const bookingChecks = useMemo(
    () => createBookingChecksRepository({ getUser: () => currentUser }),
    [currentUser]
  )
  const tenantId = tenant.selectedClinic?.id ?? ''
  const patientResult = usePatients(patientSource, tenantId, {
    prefetchNext: false,
  })
  const requestedPatient = searchParams.get('patient')
  const requestedPatientResult = usePatient(
    patientSource,
    tenantId,
    requestedPatient ?? ''
  )
  if (!tenant.selectedClinic) return null
  const patientContext =
    requestedPatientResult.status === 'ready'
      ? requestedPatientResult.patient
      : null
  return (
    <AppointmentsContent
      key={`${tenantId}:${attempt}`}
      tenantId={tenantId}
      repository={repository}
      actionRepository={actions}
      bookingRepository={booking}
      patientsRepository={patientSource}
      providerResourceRepository={bookingCapabilitySource}
      bookingChecksRepository={bookingChecks}
      patients={patientResult.patients}
      serviceIntents={patientResult.serviceIntents}
      patientsStatus={patientResult.status}
      patientContext={patientContext}
      onNavigate={(path) => void navigate(path)}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  )
}
